import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { ApiFailure, api } from "../lib/api";
import { Button, ErrorNote, Field, inputClass } from "../components/ui";

/**
 * Sign in.
 *
 * Three phases in one place — credentials, the optional 2FA code, and the
 * hand-off — because the middle one depends on a half-authenticated session
 * that exists for a moment and cannot survive a refresh, so it must never have
 * a URL of its own.
 *
 * The hand-off is the part worth explaining. Signing in is not instant: the
 * console has to learn who you are, read the operator settings, open the event
 * stream and ask the gateway how it is doing. That work happens regardless.
 * Showing it — rather than a spinner over a blank screen — turns dead time into
 * the app visibly getting ready.
 *
 * Every step below is a real request. None is a timer pretending to be work;
 * each ticks when its own call resolves, and a slow network makes the sequence
 * genuinely slower. Faking it would be a lie the first slow day would expose.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [phase, setPhase] = useState<"credentials" | "totp" | "preparing">("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ totpRequired: boolean }>("/auth/sign-in", { email, password });
      setPhase(result.totpRequired ? "totp" : "preparing");
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not reach the server.");
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/totp", { code });
      setPhase("preparing");
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not reach the server.");
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative grid min-h-full place-items-center overflow-hidden p-6">
      <AmbientField />

      <div className="relative z-10 w-full max-w-[400px]">
        <Brandmark settled={phase === "preparing"} />

        <div
          className="animate-fade-up relative overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--surface-1)]/80 p-6 shadow-[var(--elevation-pop)] backdrop-blur-2xl"
          style={{ animationDelay: "120ms" }}
        >
          {/* A one-pixel highlight along the top edge, fading out at the
              corners. Light falls from above, so the upper border catches it —
              the small physical cue that separates a designed surface from a
              rectangle with a border. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-6 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, var(--crystal-hi), transparent)",
              opacity: 0.5,
            }}
          />

          {phase === "credentials" && (
            <form onSubmit={submitCredentials} className="grid gap-4">
              <div className="animate-fade-up" style={{ animationDelay: "200ms" }}>
                <Field label="Email">
                  <input
                    className={inputClass}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    required
                  />
                </Field>
              </div>
              <div className="animate-fade-up" style={{ animationDelay: "280ms" }}>
                <Field label="Password">
                  <input
                    className={inputClass}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </Field>
              </div>
              {error && <ErrorNote>{error}</ErrorNote>}
              <div className="animate-fade-up" style={{ animationDelay: "360ms" }}>
                <Button type="submit" variant="primary" disabled={busy} className="w-full py-2.5">
                  {busy ? "Checking…" : "Sign in"}
                </Button>
              </div>
            </form>
          )}

          {phase === "totp" && (
            <form onSubmit={submitTotp} className="animate-fade-up grid gap-4">
              <p className="text-[13px] leading-relaxed text-[var(--muted)]">
                Your password was accepted. Enter the six-digit code from your authenticator app.
              </p>
              <Field label="Code">
                <input
                  className={`${inputClass} tabular text-center text-[22px] tracking-[0.45em]`}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  required
                />
              </Field>
              {error && <ErrorNote>{error}</ErrorNote>}
              <Button
                type="submit"
                variant="primary"
                disabled={busy || code.length !== 6}
                className="w-full py-2.5"
              >
                {busy ? "Checking…" : "Verify"}
              </Button>
            </form>
          )}

          {phase === "preparing" && <Preparing onReady={onSignedIn} />}
        </div>

        {phase !== "preparing" && (
          <p
            className="animate-fade-up mt-5 text-center text-[12px] leading-relaxed text-[var(--faint)]"
            style={{ animationDelay: "440ms" }}
          >
            Access is limited to authorised accounts.
            <br />
            The API reference is at{" "}
            <a className="text-[var(--brand)] transition-colors hover:text-[var(--crystal-hi)]" href="/docs">
              /docs
            </a>
            .
          </p>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The hand-off                                                              */
/* -------------------------------------------------------------------------- */

type StepState = "waiting" | "running" | "done" | "skipped";

interface Step {
  readonly label: string;
  readonly run: () => Promise<void>;
}

function Preparing({ onReady }: { onReady: () => void }) {
  const [states, setStates] = useState<StepState[]>([]);
  const started = useRef(false);

  /**
   * Each step performs work the console needs anyway, and resolves when that
   * work is actually finished.
   *
   * A step that fails is marked skipped rather than fatal: a viewer has no
   * `config:read` scope, so reading settings legitimately 403s for them, and
   * refusing to complete the sign-in over it would lock out the account that
   * is behaving correctly.
   */
  const steps: Step[] = [
    {
      label: "Confirming your identity",
      run: async () => {
        await api.get("/auth/me");
      },
    },
    {
      label: "Loading approval rules",
      run: async () => {
        await api.get("/admin/settings");
      },
    },
    {
      label: "Reading the queue",
      run: async () => {
        await api.get("/stats/overview");
      },
    },
    {
      label: "Reaching the Discord gateway",
      run: async () => {
        await api.get("/stats/bot");
      },
    },
    {
      label: "Opening the live stream",
      run: () =>
        new Promise<void>((resolve) => {
          const source = new EventSource("/api/v1/stream?events=bot.status_changed", {
            withCredentials: true,
          });
          // Whichever comes first: the stream opens, or it does not and the
          // console carries on without live updates rather than hanging here.
          const finish = () => {
            source.close();
            resolve();
          };
          source.addEventListener("open", finish);
          source.addEventListener("error", finish);
          setTimeout(finish, 3000);
        }),
    },
  ];

  const run = useCallback(async () => {
    setStates(steps.map(() => "waiting"));

    for (const [index, step] of steps.entries()) {
      setStates((prev) => prev.map((s, i) => (i === index ? "running" : s)));
      let outcome: StepState = "done";
      try {
        await step.run();
      } catch {
        outcome = "skipped";
      }
      // A floor of 260ms per step. Not padding for its own sake: on a fast
      // connection every request resolves in under 40ms, and five states
      // flashing past inside a fifth of a second reads as a glitch rather than
      // as progress. The floor only ever applies when the work was faster.
      await new Promise((r) => setTimeout(r, 260));
      setStates((prev) => prev.map((s, i) => (i === index ? outcome : s)));
    }

    // A beat on the completed list before handing over, so the last tick is
    // seen rather than inferred.
    await new Promise((r) => setTimeout(r, 420));
    onReady();
  }, [onReady]);

  useEffect(() => {
    // React 18's StrictMode double-invokes effects in development; without this
    // the whole sequence would run twice.
    if (started.current) return;
    started.current = true;
    void run();
  }, [run]);

  const done = states.filter((s) => s === "done" || s === "skipped").length;
  const progress = states.length === 0 ? 0 : (done / states.length) * 100;

  return (
    <div className="grid gap-4">
      <div>
        <h2 className="text-[15px] font-semibold">Preparing your console</h2>
        <p className="mt-1 text-[13px] text-[var(--muted)]">This takes a moment. Nothing to do.</p>
      </div>

      {/* The rail carries two things at once: how far along we are, and that
          something is still happening. The sweep is what stops a stalled
          request from looking like a frozen page. */}
      <div className="relative h-1 overflow-hidden rounded-full bg-[var(--surface-2)]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-[var(--brand)] transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
        {progress < 100 && (
          <div
            className="animate-sweep absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[var(--crystal-hi)]/40 to-transparent"
            aria-hidden="true"
          />
        )}
      </div>

      <ul className="grid gap-2.5" aria-live="polite">
        {steps.map((step, i) => {
          const state = states[i] ?? "waiting";
          return (
            <li
              key={step.label}
              className="flex items-center gap-2.5 text-[13px] transition-opacity duration-300"
              style={{ opacity: state === "waiting" ? 0.4 : 1 }}
            >
              <StepIcon state={state} />
              <span className={state === "waiting" ? "text-[var(--faint)]" : "text-[var(--foreground)]"}>
                {step.label}
              </span>
              {state === "skipped" && (
                <span className="ml-auto text-[11px] text-[var(--faint)]">not available to you</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StepIcon({ state }: { state: StepState }) {
  if (state === "running") {
    return (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6" fill="none" stroke="var(--surface-2)" strokeWidth="2" />
        <circle
          cx="8"
          cy="8"
          r="6"
          fill="none"
          stroke="var(--brand)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="10 28"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 8 8"
            to="360 8 8"
            dur="0.8s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    );
  }

  if (state === "done") {
    return (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="var(--brand-soft)" />
        <path
          d="M5 8.2 L7.1 10.3 L11 6.2"
          fill="none"
          stroke="var(--brand)"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          // The tick draws itself rather than appearing, so a completed step is
          // an event you can see happen.
          style={{ strokeDasharray: 14, strokeDashoffset: 14, animation: "draw 300ms ease-out forwards" }}
        />
      </svg>
    );
  }

  if (state === "skipped") {
    return (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="var(--surface-2)" />
        <path d="M5.5 8 H10.5" stroke="var(--faint)" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6" fill="none" stroke="var(--surface-2)" strokeWidth="2" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/*  Ornament                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The mark.
 *
 * A hexagon that draws itself on load and closes once you are through — the
 * shape of a gate, which is what this product is. The ring settles from dashed
 * to solid at the hand-off, so the transition is legible without a word of copy.
 */
function Brandmark({ settled }: { settled: boolean }) {
  return (
    <div className="animate-fade-up mb-7 flex items-center gap-3.5">
      <div className="relative h-11 w-11 shrink-0">
        <svg viewBox="0 0 44 44" className="h-11 w-11" aria-hidden="true">
          <defs>
            <linearGradient id="crystal" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--crystal-hi)" />
              <stop offset="100%" stopColor="var(--crystal-lo)" />
            </linearGradient>
          </defs>
          <path
            d="M22 3 L37.6 12 L37.6 32 L22 41 L6.4 32 L6.4 12 Z"
            fill="url(#crystal)"
            opacity={0.14}
          />
          <path
            d="M22 3 L37.6 12 L37.6 32 L22 41 L6.4 32 L6.4 12 Z"
            fill="none"
            stroke="url(#crystal)"
            strokeWidth="1.6"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 108,
              strokeDashoffset: 108,
              animation: "draw 900ms cubic-bezier(0.16,1,0.3,1) 120ms forwards",
            }}
          />
          <path
            d="M15.5 22 L20 26.5 L28.5 18"
            fill="none"
            stroke="var(--brand)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 20,
              strokeDashoffset: settled ? 20 : 20,
              animation: settled
                ? "draw 420ms cubic-bezier(0.16,1,0.3,1) forwards"
                : "draw 520ms cubic-bezier(0.16,1,0.3,1) 700ms forwards",
            }}
          />
        </svg>
        {settled && (
          <span className="animate-ping-ring absolute inset-0 rounded-full bg-[var(--brand)] opacity-30" />
        )}
      </div>
      <div className="leading-tight">
        <div className="text-[18px] font-semibold tracking-[-0.01em]">SentryQ Bot</div>
        <div className="text-[13px] text-[var(--faint)]">Approval console</div>
      </div>
    </div>
  );
}

/**
 * The ambient field behind everything.
 *
 * Two slow-drifting radial washes and a faint grid. It animates `transform`
 * only, at a very low frequency, so it costs almost nothing and never competes
 * with the form — the point is depth, not a light show. It is decorative, so
 * `prefers-reduced-motion` stops it dead and it stays a static gradient.
 */
function AmbientField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className="animate-drift absolute -left-[10%] -top-[20%] h-[620px] w-[620px] rounded-full opacity-[0.30] blur-[110px]"
        style={{ background: "radial-gradient(circle, var(--crystal) 0%, transparent 68%)" }}
      />
      <div
        className="animate-drift absolute -bottom-[25%] -right-[10%] h-[680px] w-[680px] rounded-full opacity-[0.22] blur-[120px]"
        style={{
          background: "radial-gradient(circle, var(--crystal-lo) 0%, transparent 68%)",
          animationDelay: "-13s",
        }}
      />
      {/* A pool of light behind where the card sits, so the card reads as
          resting in the scene rather than pasted onto a flat backdrop. */}
      <div
        className="absolute left-1/2 top-1/2 h-[440px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.10] blur-[90px]"
        style={{ background: "radial-gradient(ellipse, var(--crystal-hi) 0%, transparent 70%)" }}
      />

      {/* A hairline grid, masked to fade at the edges so it never reads as a
          hard-edged texture panel. */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 45%, black 20%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 45%, black 20%, transparent 100%)",
        }}
      />
    </div>
  );
}
